import api from './axiosConfig';

export type ScannerMarketScope = 'ALL' | 'KOSPI' | 'KOSDAQ';
export type ScannerUniverseScope = 'REALTIME_COVERAGE' | 'STRONG_SECTOR_12';
export type ScannerFieldCode =
    | 'CHANGE_RATE'
    | 'OPEN_CHANGE_RATE'
    | 'HIGH_DISTANCE_RATE'
    | 'LOW_REBOUND_RATE'
    | 'TRADING_VALUE'
    | 'RELATIVE_TRADING_VALUE'
    | 'VOLUME'
    | 'RELATIVE_VOLUME'
    | 'PRICE'
    | 'FOREIGN_NET_BUY_AMOUNT'
    | 'INSTITUTION_NET_BUY_AMOUNT'
    | 'SMART_MONEY_NET_BUY_AMOUNT'
    | 'PROGRAM_NET_BUY_EXEC_AMOUNT';
export type ScannerOperatorCode = 'GTE' | 'GT' | 'LTE' | 'LT';

export type ScannerCondition = {
  id?: number;
  fieldCode: ScannerFieldCode | string;
  fieldLabel?: string;
  operatorCode: ScannerOperatorCode | string;
  numericValue: number;
  unit?: string;
};

export type ScannerRule = {
  id: number;
  ruleSource?: 'USER' | 'SYSTEM_PRESET' | string;
  managed?: boolean;
  presetCode?: string;
  presetDescription?: string;
  name: string;
  marketScope: ScannerMarketScope | string;
  universeScope: ScannerUniverseScope | string;
  enabled: boolean;
  webPushEnabled: boolean;
  matchMode: 'ALL' | string;
  lastEvaluatedAt?: string;
  lastCandidateCount?: number;
  lastMatchCount?: number;
  createdAt?: string;
  updatedAt?: string;
  conditions: ScannerCondition[];
};

export type ScannerMatch = {
  ruleId: number;
  ruleName: string;
  marketScope?: string;
  universeScope?: string;
  ticker: string;
  name?: string;
  market?: string;
  episodeNo?: number;
  enteredAt?: string;
  lastMatchedAt?: string;
  lastNotifiedAt?: string;
  price?: number | null;
  changeRate?: number | null;
  openChangeRate?: number | null;
  highDistanceRate?: number | null;
  lowReboundRate?: number | null;
  tradingValue?: number | null;
  relativeTradingValue?: number | null;
  volume?: number | null;
  relativeVolume?: number | null;
  foreignNetBuyAmount?: number | null;
  institutionNetBuyAmount?: number | null;
  smartMoneyNetBuyAmount?: number | null;
  programNetBuyExecAmount?: number | null;
  strongSectorMember?: boolean;
  sectorName?: string;
  sectorChangeRate?: number | null;
  sectorRank?: number;
  lastEventAt?: string;
};

export type ScannerCapabilityField = {
  code: ScannerFieldCode | string;
  label: string;
  unit: string;
};

export type ScannerCapabilityOperator = {
  code: ScannerOperatorCode | string;
  label: string;
  symbol?: string;
  description?: string;
};

export type ScannerCapabilities = {
  fields: ScannerCapabilityField[];
  operators: ScannerCapabilityOperator[];
  markets: ScannerMarketScope[];
  matchMode: string;
  universeScopes?: Array<{ code: ScannerUniverseScope | string; label: string }>;
  discoveryMode?: string;
  coverageNote?: string;
};

export type ScannerDashboard = {
  rules: ScannerRule[];
  matches: ScannerMatch[];
  capabilities: ScannerCapabilities;
  generatedAt?: string;
};


export type ScannerPreviewMatch = {
  ticker: string;
  name?: string;
  market?: string;
  price?: number | null;
  changeRate?: number | null;
  sectorName?: string;
  lastEventAt?: string;
};

export type ScannerPreviewPreset = {
  presetCode: string;
  name: string;
  description?: string;
  matchCount: number;
  matches: ScannerPreviewMatch[];
};

export type ScannerPreviewResponse = {
  presets: ScannerPreviewPreset[];
  uniqueMatchCount: number;
  generatedAt?: string;
};

export type ScannerRulePayload = {
  name: string;
  marketScope: ScannerMarketScope;
  universeScope: ScannerUniverseScope;
  enabled: boolean;
  webPushEnabled: boolean;
  conditions: Array<{
    fieldCode: ScannerFieldCode | string;
    operatorCode: ScannerOperatorCode | string;
    numericValue: number;
  }>;
};


export async function getScannerPreview(signal?: AbortSignal): Promise<ScannerPreviewResponse> {
  const response = await api.get<ScannerPreviewResponse>('/live-summary/scanner/preview', {
    withCredentials: true,
    timeout: 8_000,
    signal,
  });
  return response.data ?? {presets: [], uniqueMatchCount: 0};
}

export async function getScannerDashboard(signal?: AbortSignal): Promise<ScannerDashboard> {
  const response = await api.get<ScannerDashboard>('/live-summary/scanner', {
    withCredentials: true,
    timeout: 8_000,
    signal,
  });
  return response.data;
}

export async function createScannerRule(payload: ScannerRulePayload): Promise<ScannerRule> {
  const response = await api.post<ScannerRule>('/live-summary/scanner/rules', payload, {
    withCredentials: true,
    timeout: 8_000,
  });
  return response.data;
}

export async function updateScannerRule(ruleId: number, payload: ScannerRulePayload): Promise<ScannerRule> {
  const response = await api.put<ScannerRule>(`/live-summary/scanner/rules/${ruleId}`, payload, {
    withCredentials: true,
    timeout: 8_000,
  });
  return response.data;
}

export async function setScannerRuleEnabled(ruleId: number, enabled: boolean): Promise<ScannerRule> {
  const response = await api.post<ScannerRule>(
      `/live-summary/scanner/rules/${ruleId}/enabled`,
      {enabled},
      {withCredentials: true, timeout: 8_000},
  );
  return response.data;
}

export async function deleteScannerRule(ruleId: number): Promise<void> {
  await api.delete(`/live-summary/scanner/rules/${ruleId}`, {
    withCredentials: true,
    timeout: 8_000,
  });
}
